// Page bar (multi-page navigation): tab switching / add / double-click rename / delete.
// Icons are always monochrome SVG, never emoji.

import { useState } from 'react';
import { useT } from '../i18n';
import { PlusIcon, XIcon } from './icons';
import type { Page } from '../types';

export interface PageBarProps {
  pages: Page[];
  currentPageId: string | null;
  onSwitch: (id: string) => void;
  onAdd: () => void;
  onRename: (id: string, name: string) => void;
  onDelete: (id: string) => void;
}

export default function PageBar({ pages, currentPageId, onSwitch, onAdd, onRename, onDelete }: PageBarProps) {
  const t = useT();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  const commit = () => {
    if (editingId && draft.trim()) onRename(editingId, draft.trim());
    setEditingId(null);
  };

  return (
    <div className="page-bar" role="tablist" aria-label={t('page.bar')}>
      {pages.map(pg => (
        <div
          key={pg.id}
          role="tab"
          aria-selected={pg.id === currentPageId}
          className={`page-tab ${pg.id === currentPageId ? 'active' : ''}`}
          onClick={() => onSwitch(pg.id)}
          onDoubleClick={() => { setEditingId(pg.id); setDraft(pg.name); }}
          title={t('page.renameHint')}
        >
          {editingId === pg.id ? (
            <input
              className="page-rename-input"
              autoFocus
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onBlur={commit}
              onKeyDown={e => {
                if (e.key === 'Enter') commit();
                if (e.key === 'Escape') setEditingId(null);
              }}
              onClick={e => e.stopPropagation()}
            />
          ) : (
            <span className="page-tab-name">{pg.name}</span>
          )}
          {pages.length > 1 && pg.id === currentPageId && editingId !== pg.id && (
            <button
              className="page-tab-del"
              title={t('page.delete')}
              aria-label={t('page.delete')}
              onClick={e => {
                e.stopPropagation();
                if (window.confirm(t('project.deleteConfirm'))) onDelete(pg.id);
              }}
            >
              <XIcon />
            </button>
          )}
        </div>
      ))}
      <button className="page-add" onClick={onAdd} title={t('page.add')} aria-label={t('page.add')}>
        <PlusIcon />
      </button>
    </div>
  );
}
